import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { idFromObject } from '@sorare/core/src/gql/idFromObject.ts';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { payment as paymentGlossary } from '@sorare/core/src/lib/glossary';

import type {
  PollingVoucher,
  PollingVoucherVariables,
} from './__generated__/usePollingVoucher.graphql.ts';

const POLLING_VOUCHER_QUERY = gql`
  query PollingVoucher($voucherId: ID!) {
    market {
      id
      voucher(id: $voucherId) {
        id
        primaryOffer {
          id
        }
      }
    }
  }
` as TypedDocumentNode<PollingVoucher, PollingVoucherVariables>;

export const usePollingVoucher = (
  onPollingEnd: (offerId?: string) => void,
  voucherId: string
) => {
  const { formatMessage } = useIntl();
  const [polling, setPolling] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout>>(null);
  const { showNotification } = useSnackNotificationContext();

  const { data } = useQuery(POLLING_VOUCHER_QUERY, {
    variables: { voucherId: idFromObject(voucherId) },
    pollInterval: polling ? 1000 : 0,
    skip: !polling,
  });
  const offerId = data?.market?.voucher?.primaryOffer?.id;

  const startPolling = () => {
    setPolling(true);
    timeout.current = setTimeout(() => {
      showNotification('errors', {
        errors: [formatMessage(paymentGlossary.confirmationTimeout)],
      });
      setPolling(false);
      onPollingEnd();
    }, 10000);
  };
  const stopPolling = () => {
    setPolling(false);
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  };

  useEffect(() => {
    if (offerId) {
      onPollingEnd(offerId);
      stopPolling();
    }
  });

  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  return { polling, startPolling, stopPolling };
};
